/** Top-level org departments (distinct from engineering sub-teams in roles.js TEAMS). */
import { ALL_ROLES, ROLE_DEFAULT_TEAM, TEAMS } from "./roles";

export const LEGACY_ENGINEERING_DEPARTMENT = "Engineering";
export const TECH_TEAM_DEPARTMENT = "Tech Team";

export const DEPARTMENTS = [
  TECH_TEAM_DEPARTMENT,
  "Marketing",
  "CSM",
  // Disabled by default — enable in Settings → Departments
  "IT",
  "HR",
];

/** Default visible departments until changed in Settings. */
export const DEFAULT_ENABLED_DEPARTMENTS = [TECH_TEAM_DEPARTMENT, "Marketing", "CSM"];

export const ENABLED_DEPARTMENTS_SESSION_KEY = "enabledDepartments";

export const DEFAULT_DEPARTMENT = TECH_TEAM_DEPARTMENT;

export function normalizeDepartment(value) {
  const trimmed = String(value || "").trim();
  if (trimmed === LEGACY_ENGINEERING_DEPARTMENT) return TECH_TEAM_DEPARTMENT;
  return trimmed;
}

/**
 * Department role catalog — industry-standard job designations mapped to RBAC roles.
 * `value` is stored on users.role; `label` is shown in department-scoped UI.
 */
export const DEPARTMENT_PROFILES = {
  "Tech Team": {
    roles: [
      { value: "developer", label: "Developer", defaultTeam: "Frontend" },
      { value: "tester", label: "QA / Tester", defaultTeam: "QA" },
      { value: "designer", label: "Designer", defaultTeam: "Design" },
      { value: "devops", label: "DevOps Engineer", defaultTeam: "DevOps" },
      { value: "analyst", label: "Business Analyst", defaultTeam: "Business" },
      { value: "product", label: "Product", defaultTeam: "Product" },
      { value: "product_manager", label: "Product Manager", defaultTeam: "Product" },
      { value: "lead", label: "Tech Lead", defaultTeam: "Mobile App" },
      { value: "manager", label: "Engineering Manager", defaultTeam: "Management" },
      { value: "hod", label: "Head of Department", defaultTeam: "Management" },
      { value: "member", label: "Team Member", defaultTeam: "Mobile App" },
      { value: "admin", label: "Admin", defaultTeam: "Management" },
    ],
    teams: [
      "Frontend", "Backend", "QA", "DevOps", "Design", "Product",
      "Full-stack", "Mobile App", "Business", "Management",
    ],
  },
  Marketing: {
    roles: [
      { value: "marketing_manager", label: "Marketing Manager / Director", defaultTeam: "Marketing" },
      { value: "hod", label: "Head of Department", defaultTeam: "Marketing" },
      { value: "brand_manager", label: "Brand Manager", defaultTeam: "Brand" },
      { value: "pr_manager", label: "PR Manager", defaultTeam: "PR" },
      { value: "content_marketer", label: "Content Marketing Specialist", defaultTeam: "Content" },
      { value: "marketing_designer", label: "Designer", defaultTeam: "Design" },
      { value: "sales_marketing", label: "Sales Representative", defaultTeam: "Sales" },
      { value: "trainee", label: "Trainee", defaultTeam: "Marketing" },
      { value: "manager", label: "Department Head", defaultTeam: "Marketing" },
    ],
    teams: ["Marketing", "Sales", "Content", "Brand", "Design", "PR"],
  },
  CSM: {
    roles: [
      { value: "manager", label: "CSM Manager / Director", defaultTeam: "CSM" },
      { value: "analyst", label: "Onboarding Specialist", defaultTeam: "Onboarding" },
      { value: "product", label: "Account Manager", defaultTeam: "Accounts" },
      { value: "member", label: "Customer Success Manager", defaultTeam: "CSM" },
      { value: "trainee", label: "Trainee", defaultTeam: "CSM" },
    ],
    teams: ["CSM", "Onboarding", "Renewals", "Accounts", "Support"],
  },
  // Disabled by default — enable in Settings → Departments
  IT: {
    roles: [
      { value: "manager", label: "IT Manager / Director", defaultTeam: "IT" },
      { value: "devops", label: "Infrastructure / DevOps Engineer", defaultTeam: "IT" },
      { value: "it_support", label: "IT Support Specialist", defaultTeam: "IT" },
    ],
    teams: ["IT"],
  },
  HR: {
    roles: [
      { value: "manager", label: "HR Manager / Director", defaultTeam: "HR" },
      { value: "hr", label: "HR Generalist / Business Partner", defaultTeam: "HR" },
      { value: "hr_recruiter", label: "Recruiter / Talent Acquisition", defaultTeam: "HR" },
    ],
    teams: ["HR"],
  },
};

export function getDepartmentProfile(department) {
  const dept = normalizeDepartment(department);
  return DEPARTMENT_PROFILES[dept] || DEPARTMENT_PROFILES[DEFAULT_DEPARTMENT];
}

export function isValidDepartment(value) {
  return DEPARTMENTS.includes(normalizeDepartment(value));
}

export function getEnabledDepartmentsFromSession() {
  try {
    const raw = sessionStorage.getItem(ENABLED_DEPARTMENTS_SESSION_KEY);
    if (!raw) return [...DEFAULT_ENABLED_DEPARTMENTS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [...DEFAULT_ENABLED_DEPARTMENTS];
    return parsed.map(normalizeDepartment).filter((d) => DEPARTMENTS.includes(d));
  } catch {
    return [...DEFAULT_ENABLED_DEPARTMENTS];
  }
}

export function syncEnabledDepartmentsSession(enabledList) {
  try {
    const normalized = (enabledList?.length ? enabledList : DEFAULT_ENABLED_DEPARTMENTS)
      .map(normalizeDepartment)
      .filter((d) => DEPARTMENTS.includes(d));
    sessionStorage.setItem(ENABLED_DEPARTMENTS_SESSION_KEY, JSON.stringify(normalized));
    return normalized;
  } catch {
    return [...DEFAULT_ENABLED_DEPARTMENTS];
  }
}

export function filterSelectableDepartments(enabledList) {
  const enabled = (enabledList?.length ? enabledList : getEnabledDepartmentsFromSession())
    .map(normalizeDepartment);
  const set = new Set(enabled);
  return DEPARTMENTS.filter((d) => set.has(normalizeDepartment(d)));
}

export function isDepartmentEnabled(dept, enabledList) {
  const list = enabledList?.length ? enabledList : getEnabledDepartmentsFromSession();
  return list.map(normalizeDepartment).includes(normalizeDepartment(dept));
}

export function getRolesForDepartment(department) {
  return getDepartmentProfile(department).roles;
}

export function getTeamsForDepartment(department) {
  return getDepartmentProfile(department).teams;
}

/** Onboarding job-role picker (same catalog as admin assign). */
export function getJobRolesForDepartment(department) {
  return getRolesForDepartment(department);
}

export function getAssignableRolesForDepartment(department) {
  return getRolesForDepartment(department).map((r) => r.value);
}

export function isJobRoleValidForDepartment(department, jobRole) {
  return getAssignableRolesForDepartment(department).includes(
    String(jobRole || "").toLowerCase().trim()
  );
}

export function getDefaultTeamForRole(role, department = DEFAULT_DEPARTMENT) {
  const r = String(role || "").toLowerCase().trim();
  const profileRole = getRolesForDepartment(department).find((x) => x.value === r);
  if (profileRole?.defaultTeam) return profileRole.defaultTeam;

  const fromMeta = ROLE_DEFAULT_TEAM[r];
  const teams = getTeamsForDepartment(department);
  if (fromMeta && teams.includes(fromMeta)) return fromMeta;
  return teams[0] || null;
}

/** Role dropdown — department labels first; falls back to global ALL_ROLES for custom entries. */
export function buildScopedRoleOptions(department, customRoles = []) {
  const profileRoles = getRolesForDepartment(department);
  const allowed = new Set(profileRoles.map((r) => r.value));
  const globalByValue = Object.fromEntries(ALL_ROLES.map((r) => [r.value, r]));
  const extras = customRoles
    .filter((cr) => allowed.has(cr.value) && !profileRoles.some((r) => r.value === cr.value))
    .map((cr) => ({
      value: cr.value,
      label: cr.label || globalByValue[cr.value]?.label || cr.value,
    }));
  return [...profileRoles, ...extras];
}

/** Team dropdown options scoped to a department. */
export function buildScopedTeamOptions(department, customTeams = []) {
  const deptTeams = getTeamsForDepartment(department);
  const fromMeta = TEAMS.filter((t) => t.value !== "Any" && deptTeams.includes(t.value));
  const covered = new Set(fromMeta.map((t) => t.value));
  const plain = deptTeams
    .filter((t) => !covered.has(t))
    .map((t) => ({ value: t, label: t }));
  const fromCustom = customTeams.filter(
    (ct) =>
      deptTeams.includes(ct.value) &&
      !covered.has(ct.value) &&
      !plain.some((p) => p.value === ct.value)
  );
  return [...fromMeta, ...plain, ...fromCustom];
}

export function isAdminRole(role) {
  return ["admin", "superadmin"].includes(String(role || "").toLowerCase());
}

/** Tech Team sub-teams (Frontend, Backend, etc.) are user-pickable; other depts auto-set from role. */
export function showsTeamPicker(department) {
  return normalizeDepartment(department) === DEFAULT_DEPARTMENT;
}

/** Bug tracker (/issues) is Tech Team-only; other departments use Sprint Board for work items. */
export function departmentUsesBugTracker(department) {
  return normalizeDepartment(department || DEFAULT_DEPARTMENT) === DEFAULT_DEPARTMENT;
}

/** Department-specific placeholders for manager-defined KPI forms. */
export function getDepartmentKpiHints(department) {
  const dept = normalizeDepartment(department || DEFAULT_DEPARTMENT);
  if (dept === "Marketing") {
    return {
      titlePlaceholder: "KPI title (e.g. MQLs, campaign launches)",
      periodPlaceholder: "Period (e.g. Q2 campaign)",
      unitPlaceholder: "Unit (e.g. leads, %)",
      panelSubtitle: "Track campaign, pipeline, and content delivery targets",
    };
  }
  if (dept === "CSM") {
    return {
      titlePlaceholder: "KPI title (e.g. NRR, onboarding completion)",
      periodPlaceholder: "Period (e.g. Q2 renewals)",
      unitPlaceholder: "Unit (e.g. accounts, %)",
      panelSubtitle: "Track onboarding, retention, and account health targets",
    };
  }
  if (dept === "HR") {
    return {
      titlePlaceholder: "KPI title (e.g. hires, program completion)",
      periodPlaceholder: "Period (e.g. FY 2026)",
      unitPlaceholder: "Unit (e.g. hires, %)",
      panelSubtitle: "Track people programs and HR deliverables",
    };
  }
  if (dept === "IT") {
    return {
      titlePlaceholder: "KPI title (e.g. ticket SLA, uptime)",
      periodPlaceholder: "Period (e.g. Q2 2026)",
      unitPlaceholder: "Unit (e.g. tickets, %)",
      panelSubtitle: "Track infrastructure and support targets",
    };
  }
  return {
    titlePlaceholder: "KPI title (e.g. sprint velocity, bug closure)",
    periodPlaceholder: "Period (e.g. Q2 2026)",
    unitPlaceholder: "Unit (e.g. pts, %)",
    panelSubtitle: "Define delivery targets and track progress over time",
  };
}

/** Value persisted to users.team — picker value for Tech Team, role default otherwise. */
export function resolveUserTeam(department, role, team) {
  if (showsTeamPicker(department)) return team || null;
  return getDefaultTeamForRole(role, department) || null;
}
